import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon
} from "@mui/icons-material";
import supabase from "../../services/supabaseClient";
import { useCategories, useFetchCategories } from "../../actions/categories";

const buildLocalDefinition = (definition) => ({
    categoryName: definition.categoryName,
    originalName: definition.categoryName,
    subcategories: Array.isArray(definition.subcategories)
        ? [...definition.subcategories]
        : [],
    draftSubcategory: "",
    isNew: Boolean(definition.isNew)
});

const createEmptyDefinition = () => ({
    categoryName: "",
    originalName: null,
    subcategories: [],
    draftSubcategory: "",
    isNew: true
});

export function CategoryDefinitionsAdmin() {
    const categories = useCategories();
    const fetchCategories = useFetchCategories();

    const [definitions, setDefinitions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (categories.length === 0) {
            fetchCategories();
            return;
        }
        setDefinitions(categories.map((item) => buildLocalDefinition(item)));
    }, [categories, fetchCategories]);

    const hasUnsavedChanges = useMemo(() => {
        if (categories.length !== definitions.length) {
            return true;
        }
        return definitions.some((definition, index) => {
            const original = categories[index];
            if (!original) return true;
            if (definition.categoryName !== original.categoryName) {
                return true;
            }
            const nextSubs = definition.subcategories.slice().sort();
            const originalSubs = Array.isArray(original.subcategories)
                ? [...original.subcategories].sort()
                : [];
            if (nextSubs.length !== originalSubs.length) {
                return true;
            }
            return nextSubs.some((value, idx) => value !== originalSubs[idx]);
        });
    }, [categories, definitions]);

    const resetStatus = () => {
        setStatus("");
        setError("");
    };

    const handleCategoryNameChange = (index, value) => {
        setDefinitions((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                categoryName: value
            };
            return next;
        });
        resetStatus();
    };

    const handleDraftChange = (index, value) => {
        setDefinitions((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                draftSubcategory: value
            };
            return next;
        });
        resetStatus();
    };

    const handleAddSubcategory = (index) => {
        setDefinitions((prev) => {
            const target = prev[index];
            if (!target) return prev;
            const draft = target.draftSubcategory.trim();
            if (!draft) return prev;
            if (target.subcategories.includes(draft)) {
                return prev;
            }
            const next = [...prev];
            next[index] = {
                ...target,
                subcategories: [...target.subcategories, draft].sort((a, b) => a.localeCompare(b)),
                draftSubcategory: ""
            };
            return next;
        });
        resetStatus();
    };

    const handleRemoveSubcategory = (categoryIndex, subcategory) => {
        setDefinitions((prev) => {
            const target = prev[categoryIndex];
            if (!target) return prev;
            const next = [...prev];
            next[categoryIndex] = {
                ...target,
                subcategories: target.subcategories.filter((item) => item !== subcategory)
            };
            return next;
        });
        resetStatus();
    };

    const handleAddCategory = () => {
        setDefinitions((prev) => [...prev, createEmptyDefinition()]);
        resetStatus();
    };

    const handleRemoveCategory = async (definition) => {
        resetStatus();
        if (!definition?.categoryName) {
            setDefinitions((prev) => prev.filter((item) => item !== definition));
            return;
        }
        setSaving(true);
        try {
            const { error: deleteError } = await supabase
                .from("category_definitions_prod")
                .delete()
                .eq("category_name", definition.categoryName);
            if (deleteError) {
                throw deleteError;
            }
            await fetchCategories();
            setStatus(`Deleted ${definition.categoryName}`);
        } catch (deleteProblem) {
            setError(deleteProblem.message || "Unable to delete category right now.");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAll = async () => {
        resetStatus();

        const originalBySubcategory = new Map();
        categories.forEach((category) => {
            (category.subcategories || []).forEach((sub) => {
                originalBySubcategory.set(sub, category.categoryName);
            });
        });

        const payload = [];
        const renamed = [];
        const nextBySubcategory = new Map();

        for (const definition of definitions) {
            const trimmedName = definition.categoryName.trim();
            if (!trimmedName) {
                setError("Category names cannot be empty.");
                return;
            }

            const filteredSubs = definition.subcategories
                .map((item) => item.trim())
                .filter((item) => item.length > 0);

            payload.push({
                category_name: trimmedName,
                subcategories: filteredSubs
            });

            filteredSubs.forEach((sub) => {
                nextBySubcategory.set(sub, trimmedName);
            });

            if (
                definition.originalName &&
                definition.originalName !== trimmedName
            ) {
                renamed.push(definition.originalName);
            }
        }

        const categoryMoves = [];
        originalBySubcategory.forEach((oldCategory, subcategory) => {
            const updatedCategory = nextBySubcategory.get(subcategory);
            if (updatedCategory && updatedCategory !== oldCategory) {
                categoryMoves.push({
                    subcategory,
                    oldCategory,
                    newCategory: updatedCategory
                });
            }
        });

        setSaving(true);
        try {
            const { error: upsertError } = await supabase
                .from("category_definitions_prod")
                .upsert(payload, { onConflict: "category_name" });
            if (upsertError) {
                throw upsertError;
            }

            for (const oldName of renamed) {
                await supabase
                    .from("category_definitions_prod")
                    .delete()
                    .eq("category_name", oldName);
            }

            let totalUpdated = 0;
            for (const move of categoryMoves) {
                const { data: movedCount, error: moveError } = await supabase.rpc(
                    "change_subcategory_to_category",
                    {
                        old_category: move.oldCategory,
                        new_category: move.newCategory,
                        target_subcategory: move.subcategory
                    }
                );
                if (moveError) {
                    throw moveError;
                }
                if (typeof movedCount === "number") {
                    totalUpdated += movedCount;
                }
            }

            await fetchCategories();

            const updateMessage = categoryMoves.length > 0
                ? ` Category assignments updated for ${totalUpdated} bar${totalUpdated === 1 ? "" : "s"}.`
                : "";

            setStatus(`Category definitions saved.${updateMessage}`);
        } catch (saveProblem) {
            setError(saveProblem.message || "Unable to save categories right now.");
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        resetStatus();
        fetchCategories();
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography component="h1" variant="h5">
                    Category Definitions
                </Typography>
                <Box display="flex" gap={1}>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={saving}
                    >
                        Refresh
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddCategory}
                        disabled={saving}
                    >
                        Add Category
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveAll}
                        disabled={saving || definitions.length === 0 || !hasUnsavedChanges}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Box>

            {status && (
                <Alert severity="success">{status}</Alert>
            )}
            {error && (
                <Alert severity="error">{error}</Alert>
            )}

            {definitions.length === 0 ? (
                <Paper elevation={0} sx={{ padding: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="textSecondary">
                        No category definitions found.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {definitions.map((definition, index) => (
                        <Paper key={definition.categoryName || `draft-${index}`} sx={{ padding: 2 }}>
                            <Stack spacing={2}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <TextField
                                        label="Category Name"
                                        value={definition.categoryName}
                                        onChange={(event) =>
                                            handleCategoryNameChange(index, event.target.value)
                                        }
                                        fullWidth
                                    />
                                    <IconButton
                                        aria-label="remove category"
                                        onClick={() => handleRemoveCategory(definition)}
                                        disabled={saving}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                                <Divider />
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {definition.subcategories.map((subcategory) => (
                                        <SubcategoryChip
                                            key={subcategory}
                                            label={subcategory}
                                            onDelete={() => handleRemoveSubcategory(index, subcategory)}
                                        />
                                    ))}
                                </Stack>
                                <Box display="flex" gap={1}>
                                    <TextField
                                        label="Add subcategory"
                                        value={definition.draftSubcategory}
                                        onChange={(event) =>
                                            handleDraftChange(index, event.target.value)
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                handleAddSubcategory(index);
                                            }
                                        }}
                                        fullWidth
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddSubcategory(index)}
                                        disabled={saving}
                                    >
                                        Add
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function SubcategoryChip({ label, onDelete }) {
    return (
        <Paper
            elevation={1}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 8px",
                gap: 0.5
            }}
        >
            <Typography variant="body2">{label}</Typography>
            <IconButton size="small" onClick={onDelete} aria-label="remove subcategory">
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Paper>
    );
}

export default CategoryDefinitionsAdmin;
